import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Swords, UserPlus, X, Eye, Search, ArrowRight, TrendingUp, TrendingDown, Minus, Repeat } from "lucide-react";

const Avatar = ({ src, name }) =>
  src ? (
    <img src={src} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
  ) : (
    <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0 text-[11px] font-display text-zinc-400">
      {(name || "?").slice(0, 1).toUpperCase()}
    </div>
  );

const H2H = ({ my, their }) => (
  <span className="font-display text-sm tabular-nums whitespace-nowrap">
    <span className="text-white">{my}</span>
    <span className="text-zinc-600 mx-1">–</span>
    <span className="text-zinc-400">{their}</span>
  </span>
);

const LeadChip = ({ r }) => {
  if (r.tied) return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-zinc-400"><Minus size={11} /> Level</span>;
  return r.leading ? (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-green-400"><TrendingUp size={11} /> +{r.diff}</span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-primary-accent"><TrendingDown size={11} /> {r.diff}</span>
  );
};

export default function RivalryBoard({ tournamentId }) {
  const [board, setBoard] = useState(null);
  const [members, setMembers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    api.get(`/tournaments/${tournamentId}/rival-board`).then((r) => setBoard(r.data)).catch(() => {});
  }, [tournamentId]);

  useEffect(() => { load(); }, [load]);

  const openAdd = async () => {
    setShowAdd(true);
    if (members.length === 0) {
      try { const { data } = await api.get(`/tournaments/${tournamentId}/members`); setMembers(data); }
      catch { /* ignore */ }
    }
  };

  const addRival = async (uid) => {
    setBusyId(uid);
    try {
      await api.post(`/tournaments/${tournamentId}/rival-picks`, { rival_user_id: uid });
      toast.success("Rival added");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add rival"); }
    finally { setBusyId(null); }
  };

  const removeRival = async (uid) => {
    setBusyId(uid);
    try {
      await api.delete(`/tournaments/${tournamentId}/rival-picks/${uid}`);
      toast.success("Rival removed");
      load();
    } catch { toast.error("Couldn't remove rival"); }
    finally { setBusyId(null); }
  };

  if (!board) return null;
  const pickedIds = new Set(board.picked.map((p) => p.user_id));
  const atMax = board.picked.length >= board.max_rivals;
  const filtered = members.filter(
    (m) => !pickedIds.has(m.user_id) && m.name.toLowerCase().includes(q.trim().toLowerCase())
  );

  return (
    <div className="surface-card p-4 md:p-6 mt-4 md:mt-6" data-testid="rivalry-board">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Swords size={18} className="text-primary-accent" />
          <h2 className="font-display text-xl md:text-2xl uppercase tracking-tight">Rivalries</h2>
          <span className="label-tag text-zinc-500">{board.picked.length}/{board.max_rivals}</span>
        </div>
        <button
          data-testid="add-rival-btn"
          onClick={openAdd}
          disabled={atMax}
          title={atMax ? `You've picked the max ${board.max_rivals} rivals` : "Add a rival"}
          className="inline-flex items-center gap-1.5 bg-primary-accent text-white px-4 py-2 text-xs uppercase tracking-widest hover:bg-[var(--primary-hover)] disabled:opacity-40"
        >
          <UserPlus size={14} /> Add Rival
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Rivals you picked */}
        <div>
          <div className="label-tag text-zinc-500 mb-2 flex items-center gap-1.5"><Swords size={12} /> Your Rivals</div>
          {board.picked.length === 0 ? (
            <div className="text-sm text-zinc-500 border border-white/5 px-3 py-4" data-testid="rivals-empty">
              Pick up to {board.max_rivals} rivals to track your head-to-head all tournament long.
            </div>
          ) : (
            <div className="space-y-1.5">
              {board.picked.map((r) => (
                <div key={r.user_id} data-testid={`rival-picked-${r.user_id}`}
                  className="flex items-center justify-between gap-3 border border-white/5 hover:border-white/15 px-3 py-2.5 transition-colors">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Avatar src={r.picture} name={r.name} />
                    <div className="min-w-0">
                      <div className="font-display tracking-wide truncate text-sm">{r.name}</div>
                      <LeadChip r={r} />
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <H2H my={board.my_points} their={r.points} />
                    <button data-testid={`remove-rival-${r.user_id}`} onClick={() => removeRival(r.user_id)} disabled={busyId === r.user_id}
                      className="text-zinc-600 hover:text-primary-accent disabled:opacity-40" title="Remove rival">
                      <X size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Players watching you */}
        <div>
          <div className="label-tag text-zinc-500 mb-2 flex items-center gap-1.5"><Eye size={12} /> Watching You</div>
          {board.watchers.length === 0 ? (
            <div className="text-sm text-zinc-500 border border-white/5 px-3 py-4" data-testid="watchers-empty">
              No one has picked you as a rival yet. Climb the board and that&apos;ll change.
            </div>
          ) : (
            <div className="space-y-1.5">
              {board.watchers.map((r) => (
                <div key={r.user_id} data-testid={`rival-watcher-${r.user_id}`}
                  className="flex items-center justify-between gap-3 border border-white/5 px-3 py-2.5">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Avatar src={r.picture} name={r.name} />
                    <div className="min-w-0">
                      <div className="font-display tracking-wide truncate text-sm flex items-center gap-1.5">
                        {r.name}
                        {r.mutual && <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-widest text-primary-accent border border-primary-accent/40 px-1"><Repeat size={9} /> Mutual</span>}
                      </div>
                      <LeadChip r={r} />
                    </div>
                  </div>
                  <H2H my={board.my_points} their={r.points} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-[210] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="add-rival-modal"
          onClick={() => setShowAdd(false)}>
          <div className="max-w-md w-full surface-card p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Swords size={16} className="text-primary-accent" />
                <span className="font-display text-lg uppercase">Add a Rival</span>
              </div>
              <button data-testid="add-rival-close" onClick={() => setShowAdd(false)}
                className="w-8 h-8 flex items-center justify-center border border-zinc-800 text-zinc-400 hover:border-white/30"><X size={14} /></button>
            </div>
            <div className="relative mb-3">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input data-testid="rival-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search players…"
                className="w-full bg-zinc-950 border border-zinc-800 pl-9 pr-3 py-2 text-sm focus:border-primary-accent focus:outline-none" />
            </div>
            <div className="max-h-72 overflow-y-auto space-y-1 pr-1">
              {filtered.length === 0 && <div className="text-sm text-zinc-500 py-4 text-center">No players found.</div>}
              {filtered.slice(0, 50).map((m) => (
                <button key={m.user_id} data-testid={`rival-option-${m.user_id}`} onClick={() => addRival(m.user_id)} disabled={busyId === m.user_id || atMax}
                  className="w-full flex items-center justify-between gap-2 border border-white/5 hover:border-primary-accent/50 px-3 py-2.5 transition-colors text-left disabled:opacity-40">
                  <span className="font-display tracking-wide truncate text-sm">{m.name}</span>
                  <ArrowRight size={14} className="text-primary-accent flex-shrink-0" />
                </button>
              ))}
            </div>
            {atMax && <div className="text-xs text-primary-accent mt-3">You&apos;ve reached the {board.max_rivals}-rival limit. Remove one to add another.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
